/** QuickPlan — Export glTF binaire (.glb) de l'assemblage.
 *
 * La scène exportée est reconstruite à partir du modèle (comme les illustrations de
 * notice) : la vue 3D affichée n'est jamais touchée, et rien de ce qui n'est pas de la
 * géométrie d'assemblage n'y figure (grille, axes, gizmo, ports, fixations figurées,
 * surbrillance de sélection, caméra, lumières). Les pièces masquées sont exclues.
 *
 * Conventions de sortie (attendues par QuickVision) :
 *  - unités en MÈTRES (QuickPlan travaille en mm) ;
 *  - Y vers le haut (convention glTF ; QuickPlan est en Z vers le haut, comme en CAO) :
 *    x' = x, y' = z, z' = −y (rotation de −90° autour de X, repère direct conservé) ;
 *  - assemblage posé au sol (min Y = 0) et centré en X/Z sur le centre de son emprise.
 *
 * Hiérarchie : nœud racine (nom du projet, identité) → sous-ensembles → instances
 * (nom de la pièce ou du composant, extras { quickplanId, componentId, category })
 * → maillages des primitives. Toutes les matrices de nœuds restent rigides (rotation +
 * translation en mètres) : l'échelle et le changement d'axes sont cuits dans les sommets.
 */
import * as THREE from 'three';
import { geometryFor, partKey } from '../viewer3d/cache.js';
import { frames, hiddenSet, resolvedOf } from '../geom.js';

const DEG = Math.PI / 180;

/** Changement de base mm / Z-haut → m / Y-haut (x' = x, y' = z, z' = −y, ÷ 1000). */
export const BASIS = new THREE.Matrix4().set(
  0.001, 0, 0, 0,
  0, 0, 0.001, 0,
  0, -0.001, 0, 0,
  0, 0, 0, 1,
);
const BASIS_INV = BASIS.clone().invert();

function frameMatrix(f) {
  return new THREE.Matrix4().set(
    f.R[0][0], f.R[0][1], f.R[0][2], f.t[0],
    f.R[1][0], f.R[1][1], f.R[1][2], f.t[1],
    f.R[2][0], f.R[2][1], f.R[2][2], f.t[2],
    0, 0, 0, 1,
  );
}

/** Conjugue une matrice locale (mm, Z-haut) dans la base de sortie : B·M·B⁻¹. */
const conj = (m) => BASIS.clone().multiply(m).multiply(BASIS_INV);

function setLocal(obj, m) {
  m.decompose(obj.position, obj.quaternion, obj.scale);
  obj.scale.set(1, 1, 1); // rigide par construction (évite les 0,999999…)
}

/**
 * Construit la scène d'export (THREE.Group racine) du projet, normalisée.
 * Fonction pure (pas de DOM) : utilisable dans les tests.
 */
export function buildExportScene(project, lookup) {
  const F = frames(project, lookup);
  const hidden = hiddenSet(project);
  const byId = new Map(project.instances.map((i) => [i.id, i]));
  const geoCache = new Map(); // partKey → géométrie convertie (partagée entre maillages)
  const matCache = new Map();

  const geometry = (p) => {
    const key = partKey(p);
    let g = geoCache.get(key);
    if (!g) {
      g = geometryFor(p).clone().applyMatrix4(BASIS);
      g.computeBoundingBox();
      geoCache.set(key, g);
    }
    return g;
  };
  const material = (color, category, opacity) => {
    const op = opacity ?? 1;
    const key = `${color}|${category}|${op}`;
    let m = matCache.get(key);
    if (!m) {
      const metal = category === 'alu' || category === 'rack' || category === 'quincaillerie';
      const c = new THREE.Color(color);
      m = new THREE.MeshStandardMaterial({
        name: `${category ?? 'piece'} #${c.getHexString()}${op < 1 ? ` ${Math.round(op * 100)}%` : ''}`,
        color: c,
        metalness: metal ? 0.45 : 0.05,
        roughness: metal ? 0.42 : 0.75,
        transparent: op < 1,
        opacity: op,
      });
      matCache.set(key, m);
    }
    return m;
  };

  const root = new THREE.Group();
  root.name = project.name || 'QuickPlan';
  const raw = new THREE.Group(); // contenu en mm / Z-haut converti nœud par nœud

  // Sous-ensembles (arborescence), uniquement ceux qui contiendront quelque chose.
  const groupNodes = new Map();
  const gById = new Map((project.groups ?? []).map((g) => [g.id, g]));
  const groupNode = (gid, guard = 0) => {
    if (!gid || !gById.has(gid) || guard > 50) return raw;
    let n = groupNodes.get(gid);
    if (!n) {
      const g = gById.get(gid);
      n = new THREE.Group();
      n.name = g.name;
      n.userData = { quickplanGroupId: g.id };
      groupNodes.set(gid, n);
      groupNode(g.parentId, guard + 1).add(n);
    }
    return n;
  };

  const nodes = new Map();
  const visible = project.instances.filter((i) => !hidden.has(i.id) && resolvedOf(i, lookup) && F.get(i.id));
  // Parents d'abord (ordre topologique simple).
  const depth = (i) => { let d = 0; let c = i; while (c?.parentId && byId.has(c.parentId) && d < 100) { d++; c = byId.get(c.parentId); } return d; };
  visible.sort((a, b) => depth(a) - depth(b));

  for (const inst of visible) {
    const comp = lookup(inst.componentId);
    const res = resolvedOf(inst, lookup);
    const parent = inst.parentId && nodes.get(inst.parentId);
    const world = frameMatrix(F.get(inst.id));
    const local = parent ? frameMatrix(F.get(inst.parentId)).invert().multiply(world) : world;
    const node = new THREE.Group();
    node.name = inst.name || comp?.name || inst.componentId;
    node.userData = { quickplanId: inst.id, componentId: inst.componentId, ...(comp?.category ? { category: comp.category } : {}) };
    setLocal(node, conj(local));
    res.parts.forEach((p, k) => {
      const mesh = new THREE.Mesh(geometry(p), material(inst.color ?? p.color ?? comp?.color ?? '#b0b4ba', comp?.category, p.opacity));
      mesh.name = res.parts.length > 1 ? `${node.name} · ${p.name || `${p.type} ${k + 1}`}` : node.name;
      const pm = new THREE.Matrix4().compose(
        new THREE.Vector3(...p.pos),
        new THREE.Quaternion().setFromEuler(new THREE.Euler(...(p.rot ?? [0, 0, 0]).map((d) => d * DEG), 'XYZ')),
        new THREE.Vector3(1, 1, 1),
      );
      setLocal(mesh, conj(pm));
      node.add(mesh);
    });
    (parent || groupNode(inst.groupId)).add(node);
    nodes.set(inst.id, node);
  }

  // Pose au sol et centrage : boîte exacte (sommets) du contenu déjà en mètres / Y-haut.
  raw.updateMatrixWorld(true);
  const box = new THREE.Box3().setFromObject(raw, true);
  if (!box.isEmpty()) {
    const c = box.getCenter(new THREE.Vector3());
    const t = new THREE.Vector3(-c.x, -box.min.y, -c.z);
    for (const child of raw.children) child.position.add(t);
  }
  for (const child of [...raw.children]) root.add(child);
  root.updateMatrixWorld(true);
  return root;
}

/** Nom de fichier : « <nom du projet>.glb » (caractères interdits remplacés). */
export function glbFileName(project) {
  const base = (project?.name ?? '').replace(/[\\/:*?"<>|\u0000-\u001f]+/g, '_').trim();
  return `${base || 'projet'}.glb`;
}

/** Produit le .glb (ArrayBuffer). L'exporteur n'est chargé qu'à la demande. */
export async function exportGlb(project, lookup) {
  const { GLTFExporter } = await import('three/examples/jsm/exporters/GLTFExporter.js');
  const root = buildExportScene(project, lookup);
  if (!root.children.length) throw new Error('Aucune pièce visible à exporter');
  const scene = new THREE.Scene();
  scene.name = 'QuickPlan';
  scene.add(root);
  return new GLTFExporter().parseAsync(scene, { binary: true, onlyVisible: true });
}

/** Exporte et télécharge `<nom du projet>.glb`. */
export async function downloadGlb(project, lookup) {
  const buf = await exportGlb(project, lookup);
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([buf], { type: 'model/gltf-binary' }));
  a.download = glbFileName(project);
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}
